import React, { useState, useEffect } from 'react';
import './OnboardingWizard.css';

const STEPS = [
  { n: 1, label: 'Business' },
  { n: 2, label: 'Services' },
  { n: 3, label: 'AI setup' },
  { n: 4, label: 'FAQs' },
  { n: 5, label: 'Done' },
];

const INDUSTRIES = [
  'Plumbing & Water','HVAC & Climate','Electrical','Construction','Landscaping','Painting & Decorating',
  'Roofing','Carpentry','Security Services','Cleaning','Pest Control','Pool Maintenance',
  'Moving & Relocation','Legal Services','Accounting & Finance','Business Consulting',
  'IT & Technology','Marketing & Advertising','Medical & Healthcare','Dental','Veterinary',
  'Real Estate','Property Management','Automotive','Towing & Recovery','Restaurant & Food Service',
  'Catering','Retail','Beauty & Wellness','Fitness','Photography','Event Planning',
  'Education & Tutoring','Insurance','Travel & Tourism','Other',
];

function auth() {
  return localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token') || '';
}

async function api(path, method = 'GET', body = null) {
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth()}` },
  };
  if (body) opts.body = JSON.stringify(body);
  const r = await fetch(path, opts);
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail || `Request failed (${r.status})`);
  }
  return r.json().catch(() => ({}));
}

function StepDot({ step, current, done }) {
  return (
    <div className={`ow-dot${current ? ' ow-dot--current' : ''}${done ? ' ow-dot--done' : ''}`}>
      {done ? '✓' : step.n}
      <span className="ow-dot__label">{step.label}</span>
    </div>
  );
}

function FieldRow({ children }) {
  return <div className="ow-field-row">{children}</div>;
}

function Field({ label, children, hint }) {
  return (
    <div className="ow-field">
      <label className="ow-field__label">{label}</label>
      {children}
      {hint && <span className="ow-field__hint">{hint}</span>}
    </div>
  );
}

function NavRow({ step, total, onBack, onNext, loading, nextLabel }) {
  return (
    <div className="ow-nav">
      {step > 1 && step < total && (
        <button className="ow-btn ow-btn--ghost" onClick={onBack} disabled={loading}>← Back</button>
      )}
      <button className="ow-btn ow-btn--primary" onClick={onNext} disabled={loading}>
        {loading ? 'Saving…' : (nextLabel || (step === total - 1 ? 'Finish →' : 'Continue →'))}
      </button>
    </div>
  );
}

export default function OnboardingWizard({ onComplete }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [biz, setBiz] = useState({ businessName: '', industry: '', serviceArea: '', yearsInBusiness: '', teamSize: '' });
  const [services, setServices] = useState([{ name: '', description: '', priceRange: '' }]);
  const [aiCfg, setAiCfg] = useState({ toneOfVoice: 'professional', responseTime: 'within-2-hours', emergencyAvailable: false });
  const [faqs, setFaqs] = useState([{ question: '', answer: '' }]);

  useEffect(() => {
    api('/business/onboarding')
      .then(d => {
        if (d.completed_at) {
          setStep(5);
        } else {
          setStep(1);
        }
      })
      .catch(() => {
        setStep(1);
      });
  }, []);

  const save = async (fn) => {
    setLoading(true);
    setError('');
    try { await fn(); } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const markStep = (s) => api('/business/onboarding/step', 'POST', { step: s });

  // ─── Step handlers ──────────────────────────────────────────────────────────

  const saveBiz = () => save(async () => {
    await api('/business/context', 'POST', {
      industry: biz.industry,
      service_area: biz.serviceArea,
      years_in_business: parseInt(biz.yearsInBusiness) || null,
      team_size: parseInt(biz.teamSize) || null,
    });
    await markStep('business_info');
    setStep(2);
  });

  const saveServices = () => save(async () => {
    const valid = services.filter(s => s.name.trim() && s.description.trim());
    if (!valid.length) throw new Error('Add at least one service with a name and description.');
    await api('/business/context', 'PATCH', {
      services_offered: valid.map(s => ({ name: s.name.trim(), description: s.description.trim(), price_range: s.priceRange?.trim() || null })),
    });
    await markStep('services');
    setStep(3);
  });

  const saveAI = () => save(async () => {
    await api('/business/context', 'PATCH', {
      tone_of_voice: aiCfg.toneOfVoice,
      typical_response_time: aiCfg.responseTime,
      emergency_available: aiCfg.emergencyAvailable,
    });
    await markStep('ai_config');
    setStep(4);
  });

  const saveFAQs = () => save(async () => {
    const valid = faqs.filter(f => f.question.trim() && f.answer.trim());
    await api('/business/context', 'PATCH', { frequently_asked_questions: valid });
    await markStep('phone_config');
    setStep(5);
  });

  const finish = () => save(async () => {
    await markStep('test_call');
    if (onComplete) onComplete();
  });

  // ─── Service / FAQ helpers ───────────────────────────────────────────────────

  const updateService = (i, k, v) => setServices(prev => { const n = [...prev]; n[i] = { ...n[i], [k]: v }; return n; });
  const removeService = (i) => setServices(prev => prev.filter((_, idx) => idx !== i));
  const updateFAQ = (i, k, v) => setFaqs(prev => { const n = [...prev]; n[i] = { ...n[i], [k]: v }; return n; });
  const removeFAQ = (i) => setFaqs(prev => prev.filter((_, idx) => idx !== i));

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="ow-root">
      {/* Step indicator */}
      <div className="ow-steps">
        {STEPS.map((s, idx) => (
          <React.Fragment key={s.n}>
            <StepDot step={s} current={step === s.n} done={step > s.n} />
            {idx < STEPS.length - 1 && <div className={`ow-steps__line${step > s.n ? ' ow-steps__line--done' : ''}`} />}
          </React.Fragment>
        ))}
      </div>

      {error && <div className="ow-error">{error}</div>}

      {/* ── Step 1: Business ── */}
      {step === 1 && (
        <div className="ow-card">
          <h2 className="ow-card__title">Tell us about your business</h2>
          <p className="ow-card__sub">This helps the AI introduce your company correctly to every caller.</p>
          <div className="ow-form">
            <Field label="Business name">
              <input className="ow-input" placeholder="Acme Plumbing Co." value={biz.businessName}
                onChange={e => setBiz(b => ({ ...b, businessName: e.target.value }))} />
            </Field>
            <Field label="Industry">
              <select className="ow-input" value={biz.industry} onChange={e => setBiz(b => ({ ...b, industry: e.target.value }))}>
                <option value="">Select your industry</option>
                {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
              </select>
            </Field>
            <Field label="Service area" hint="e.g. Johannesburg and surrounding areas">
              <input className="ow-input" placeholder="Johannesburg, Gauteng" value={biz.serviceArea}
                onChange={e => setBiz(b => ({ ...b, serviceArea: e.target.value }))} />
            </Field>
            <FieldRow>
              <Field label="Years in business">
                <input className="ow-input" type="number" placeholder="5" value={biz.yearsInBusiness}
                  onChange={e => setBiz(b => ({ ...b, yearsInBusiness: e.target.value }))} />
              </Field>
              <Field label="Team size">
                <input className="ow-input" type="number" placeholder="10" value={biz.teamSize}
                  onChange={e => setBiz(b => ({ ...b, teamSize: e.target.value }))} />
              </Field>
            </FieldRow>
          </div>
          <NavRow step={1} total={5} onNext={saveBiz} loading={loading} />
        </div>
      )}

      {/* ── Step 2: Services ── */}
      {step === 2 && (
        <div className="ow-card">
          <h2 className="ow-card__title">What services do you offer?</h2>
          <p className="ow-card__sub">The AI will use this to answer pricing and service questions accurately.</p>
          <div className="ow-form">
            {services.map((svc, i) => (
              <div key={i} className="ow-list-item">
                <div className="ow-list-item__header">
                  <span className="ow-list-item__n">Service {i + 1}</span>
                  {services.length > 1 && <button className="ow-remove" onClick={() => removeService(i)}>Remove</button>}
                </div>
                <Field label="Service name">
                  <input className="ow-input" placeholder="Emergency Plumbing Repair" value={svc.name} onChange={e => updateService(i, 'name', e.target.value)} />
                </Field>
                <Field label="Description">
                  <textarea className="ow-input ow-textarea" rows={2} placeholder="24/7 emergency plumbing for leaks, clogs, and more" value={svc.description} onChange={e => updateService(i, 'description', e.target.value)} />
                </Field>
                <Field label="Price range" hint="optional">
                  <input className="ow-input" placeholder="R1 500 – R3 000" value={svc.priceRange} onChange={e => updateService(i, 'priceRange', e.target.value)} />
                </Field>
              </div>
            ))}
            <button className="ow-add-btn" onClick={() => setServices(s => [...s, { name: '', description: '', priceRange: '' }])}>
              + Add another service
            </button>
          </div>
          <NavRow step={2} total={5} onBack={() => setStep(1)} onNext={saveServices} loading={loading} />
        </div>
      )}

      {/* ── Step 3: AI setup ── */}
      {step === 3 && (
        <div className="ow-card">
          <h2 className="ow-card__title">Configure your AI receptionist</h2>
          <p className="ow-card__sub">Fine-tune how the AI speaks and what it tells customers about response times.</p>
          <div className="ow-form">
            <Field label="Tone of voice" hint="How should the AI communicate?">
              <select className="ow-input" value={aiCfg.toneOfVoice} onChange={e => setAiCfg(c => ({ ...c, toneOfVoice: e.target.value }))}>
                <option value="professional">Professional — clear, precise, business-like</option>
                <option value="friendly">Friendly — warm, approachable, helpful</option>
                <option value="casual">Casual — relaxed and conversational</option>
                <option value="formal">Formal — formal language, titles used</option>
              </select>
            </Field>
            <Field label="Typical response time" hint="When can customers expect a human to follow up?">
              <select className="ow-input" value={aiCfg.responseTime} onChange={e => setAiCfg(c => ({ ...c, responseTime: e.target.value }))}>
                <option value="within-1-hour">Within 1 hour</option>
                <option value="within-2-hours">Within 2 hours</option>
                <option value="within-24-hours">Within 24 hours</option>
                <option value="next-business-day">Next business day</option>
              </select>
            </Field>
            <div className="ow-toggle-row">
              <div>
                <div className="ow-toggle-label">Emergency services available 24/7</div>
                <div className="ow-toggle-sub">The AI will tell callers emergency help is available any time</div>
              </div>
              <button
                className={`ow-toggle${aiCfg.emergencyAvailable ? ' ow-toggle--on' : ''}`}
                onClick={() => setAiCfg(c => ({ ...c, emergencyAvailable: !c.emergencyAvailable }))}
                type="button"
              >
                <span className="ow-toggle__knob" />
              </button>
            </div>
          </div>
          <NavRow step={3} total={5} onBack={() => setStep(2)} onNext={saveAI} loading={loading} />
        </div>
      )}

      {/* ── Step 4: FAQs ── */}
      {step === 4 && (
        <div className="ow-card">
          <h2 className="ow-card__title">Add your FAQs</h2>
          <p className="ow-card__sub">Give the AI answers to the questions you get most often. You can add more later.</p>
          <div className="ow-form">
            {faqs.map((faq, i) => (
              <div key={i} className="ow-list-item">
                <div className="ow-list-item__header">
                  <span className="ow-list-item__n">FAQ {i + 1}</span>
                  {faqs.length > 1 && <button className="ow-remove" onClick={() => removeFAQ(i)}>Remove</button>}
                </div>
                <Field label="Question">
                  <input className="ow-input" placeholder="What are your business hours?" value={faq.question} onChange={e => updateFAQ(i, 'question', e.target.value)} />
                </Field>
                <Field label="Answer">
                  <textarea className="ow-input ow-textarea" rows={2} placeholder="We're open Mon–Fri 8am–6pm and Sat 9am–3pm." value={faq.answer} onChange={e => updateFAQ(i, 'answer', e.target.value)} />
                </Field>
              </div>
            ))}
            <button className="ow-add-btn" onClick={() => setFaqs(f => [...f, { question: '', answer: '' }])}>
              + Add another FAQ
            </button>
          </div>
          <NavRow step={4} total={5} onBack={() => setStep(3)} onNext={saveFAQs} loading={loading} />
        </div>
      )}

      {/* ── Step 5: Complete ── */}
      {step === 5 && (
        <div className="ow-card ow-card--success">
          <div className="ow-success-icon">✓</div>
          <h2 className="ow-card__title">Your AI receptionist is ready.</h2>
          <p className="ow-card__sub">Here's what to do next to go live.</p>
          <div className="ow-next-steps">
            {[
              { icon: '📞', title: 'Test your AI', body: 'Call your number or dial an internal extension to hear the AI in action.' },
              { icon: '💬', title: 'Connect WhatsApp', body: 'Go to Social Channels and connect your WhatsApp Business number via OAuth.' },
              { icon: '📊', title: 'Watch your inbox', body: 'All calls, messages, and bookings appear in your Unified Inbox in real time.' },
            ].map(s => (
              <div key={s.title} className="ow-next-step">
                <div className="ow-next-step__icon">{s.icon}</div>
                <div>
                  <div className="ow-next-step__title">{s.title}</div>
                  <div className="ow-next-step__body">{s.body}</div>
                </div>
              </div>
            ))}
          </div>
          <button className="ow-btn ow-btn--primary ow-btn--full" onClick={finish} disabled={loading}>
            {loading ? 'One moment…' : 'Go to dashboard →'}
          </button>
        </div>
      )}
    </div>
  );
}
